import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { TextArea } from "@/components/ui/Textarea";
import { Text } from "@/components/ui/Text";
import { cn, firstPartyHosts } from "@/lib/utils";
import { ToolCallMessagePartProps } from "@assistant-ui/react";
import { Icon } from "@/components/ui/Icon";
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Shuffle,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAssistantDraft } from "../useAssistantDraft";
import {
  GENERIC_PERSONALITIES,
  PERSONALITIES,
  TEAM_PERSONALITIES,
  getPersonality,
} from "../personalities";
import { buildSlackManifest } from "../slackManifest";
import {
  DEFAULT_SLACK_REPLY_MODE,
  SLACK_CAPABILITY_GROUPS,
  SLACK_EVENT_GROUPS,
  isSlackReplyMode,
  slackReplyModeLabel,
  type SlackReplyMode,
} from "../slackCapabilities";
import { SlackReplyModeOptions } from "../SlackReplyModeOptions";

type Status = ToolCallMessagePartProps["status"];

function isExecuting(status: Status) {
  return status.type === "running" || status.type === "requires-action";
}

function ToolCard({
  title,
  icon,
  children,
  tone = "default",
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  tone?: "default" | "success" | "info";
}) {
  return (
    <div
      className={cn(
        "border-border bg-card my-4 max-w-2xl border shadow-sm",
        tone === "success" && "border-emerald-300/40 bg-emerald-50/30",
        tone === "info" && "border-sky-300/40 bg-sky-50/30",
      )}
    >
      <div className="border-border flex items-center gap-2 border-b px-5 py-3">
        {icon}
        <Text variant="body" className="font-medium">
          {title}
        </Text>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

type SecretKey = {
  name: string;
  label?: string;
  description?: string;
  sensitive?: boolean;
  placeholder?: string;
};

type SecretsArgs = {
  reason?: string;
  keys: SecretKey[];
};

type SecretsResult = {
  ok?: boolean;
  saved?: boolean;
  environment_slug?: string;
  saved_keys?: string[];
  declared_keys?: string[];
  cancelled?: boolean;
  error?: string;
};

export function RequestEnvironmentSecretsComponent({
  args,
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const draft = useAssistantDraft();
  const a = (args ?? {}) as Partial<SecretsArgs>;
  const keys = Array.isArray(a.keys) ? a.keys : [];
  const envSlug = draft.assistantEnv?.slug ?? "";
  const reason = a.reason;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(keys.map((k) => [k.name, ""])),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPending = isExecuting(status);
  const settled = !isPending;
  const r = result as SecretsResult | undefined;

  useEffect(() => {
    if (!isPending) return;
    return () => {
      draft.resolvePending(toolCallId, {
        success: false,
        cancelled: true,
      });
    };
  }, [draft, toolCallId, isPending]);

  if (settled && r?.saved) {
    const savedKeys = r.saved_keys ?? [];
    return (
      <ToolCard
        title="Environment secrets saved"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          Saved to <code>{r.environment_slug}</code>:{" "}
          {savedKeys.length === 0 ? (
            <em>no values provided; keys declared as empty.</em>
          ) : (
            savedKeys.map((k) => (
              <code key={k} className="bg-muted mr-1 px-1.5 py-0.5">
                {k}
              </code>
            ))
          )}
        </Text>
      </ToolCard>
    );
  }

  if (settled && r?.cancelled) {
    return (
      <ToolCard title="Environment secrets — skipped">
        <Text small muted>
          You can add these later from the Environments page.
        </Text>
      </ToolCard>
    );
  }

  if (settled) {
    return (
      <ToolCard title="Environment secrets — error">
        <Text small className="text-red-600">
          {r?.error ?? "Form was closed without saving."}
        </Text>
      </ToolCard>
    );
  }

  const anyFilled = keys.some((k) => (values[k.name] ?? "").length > 0);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const ok = draft.resolvePending(toolCallId, {
        success: true,
        values,
      });
      if (!ok) {
        setError("This form is no longer connected to the assistant.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => {
    draft.resolvePending(toolCallId, {
      success: false,
      cancelled: true,
    });
  };

  return (
    <ToolCard
      title={envSlug ? `Add secrets to ${envSlug}` : "Add secrets"}
      icon={<Icon name="key-round" className="text-muted-foreground h-4 w-4" />}
    >
      {reason && (
        <Text small muted className="mb-3">
          {reason}
        </Text>
      )}
      <div className="space-y-3">
        {keys.map((k) => (
          <div key={k.name}>
            <Label className="mb-1 block text-xs">
              {k.label ?? k.name}{" "}
              <code className="text-muted-foreground">{k.name}</code>
            </Label>
            {k.description && (
              <Text small muted className="mb-1">
                {k.description}
              </Text>
            )}
            <Input
              type={k.sensitive ? "password" : "text"}
              value={values[k.name] ?? ""}
              onChange={(v) => setValues((prev) => ({ ...prev, [k.name]: v }))}
              placeholder={k.placeholder ?? ""}
            />
          </div>
        ))}
      </div>
      {error && (
        <Text small className="mt-2 text-red-600">
          {error}
        </Text>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={cancel} disabled={submitting}>
          Skip
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={!anyFilled || submitting}
        >
          {submitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save secrets
        </Button>
      </div>
    </ToolCard>
  );
}

type WebhookArgs = {
  webhook_url: string;
  trigger_name?: string;
  instructions?: string;
};

export function ShowWebhookUrlComponent({
  args,
}: ToolCallMessagePartProps): JSX.Element {
  const a = (args ?? {}) as Partial<WebhookArgs>;
  const url = a.webhook_url ?? "";
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <ToolCard
      title={a.trigger_name ? `Webhook for ${a.trigger_name}` : "Webhook URL"}
      tone="info"
      icon={<Icon name="webhook" className="text-muted-foreground h-4 w-4" />}
    >
      {a.instructions && (
        <Text small muted className="mb-3 whitespace-pre-line">
          {a.instructions}
        </Text>
      )}
      <div className="border-border bg-muted/30 flex items-center gap-2 border px-3 py-2">
        <code className="flex-1 truncate font-mono text-xs">{url}</code>
        <Button size="sm" variant="tertiary" onClick={() => void copy()}>
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </ToolCard>
  );
}

type SlackAppArgs = {
  app_name?: string;
  workspace_hint?: string;
  bot_scopes?: string[];
  bot_events?: string[];
  webhook_url?: string;
};

type SlackAppGuideResult = {
  success: boolean;
  installed?: boolean;
  cancelled?: boolean;
};

export function ShowSlackAppGuideComponent({
  args,
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const a = (args ?? {}) as Partial<SlackAppArgs>;
  const draft = useAssistantDraft();
  const assistantName = draft.assistant?.name;

  const manifestResult = useMemo(
    () =>
      buildSlackManifest({
        appName: a.app_name ?? assistantName ?? "Platform Assistant",
        webhookUrl: a.webhook_url,
        extraScopes: a.bot_scopes,
        extraBotEvents: a.bot_events,
        unfurlDomains: firstPartyHosts(window.location.origin),
      }),
    [a.app_name, a.webhook_url, a.bot_scopes, a.bot_events, assistantName],
  );

  const webhookLive = !!a.webhook_url;
  const { deepLink } = manifestResult;

  const isPending = isExecuting(status);
  const settled = !isPending;
  const r = result as SlackAppGuideResult | undefined;

  useEffect(() => {
    if (!isPending) return;
    return () => {
      draft.resolvePending(toolCallId, {
        success: false,
        cancelled: true,
      });
    };
  }, [draft, toolCallId, isPending]);

  if (settled && r?.installed) {
    return (
      <ToolCard
        title="Slack connection installed"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          Next: paste your tokens.
        </Text>
      </ToolCard>
    );
  }

  if (settled && r?.cancelled) {
    return (
      <ToolCard title="Slack install — skipped">
        <Text small muted>
          You can come back to this anytime — just ask me to retry the Slack
          setup.
        </Text>
      </ToolCard>
    );
  }

  const markInstalled = () => {
    draft.resolvePending(toolCallId, { success: true, installed: true });
  };
  const skip = () => {
    draft.resolvePending(toolCallId, { success: false, cancelled: true });
  };

  const steps: React.ReactNode[] = [
    <>
      Click <strong>Open Slack</strong> below.
    </>,
    <>
      Pick the workspace this assistant should live in, then click{" "}
      <strong>Create</strong>.
    </>,
    <>
      In Slack's left sidebar, click <strong>Install App</strong>, then{" "}
      <strong>Install to Workspace</strong>, then approve.
    </>,
    webhookLive ? (
      <>
        In Slack's left sidebar, click <strong>Event Subscriptions</strong>,
        click <strong>Retry</strong> next to the request URL, then click{" "}
        <strong>Save Changes</strong>.
      </>
    ) : null,
    <>
      Come back here and click <strong>I'm done</strong> below.
    </>,
  ].filter(Boolean);

  return (
    <ToolCard
      title="Install your Slack connection"
      tone="info"
      icon={<Icon name="bot" className="text-muted-foreground h-4 w-4" />}
    >
      <ol className="space-y-3 text-sm leading-relaxed">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-muted-foreground shrink-0 tabular-nums">
              {i + 1}.
            </span>
            <div className="flex-1">{step}</div>
          </li>
        ))}
      </ol>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Button asChild>
          <a href={deepLink} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            Open Slack
          </a>
        </Button>
        <div className="flex gap-2">
          <Button variant="tertiary" onClick={skip}>
            Skip
          </Button>
          <Button onClick={markInstalled}>I'm done</Button>
        </div>
      </div>
    </ToolCard>
  );
}

type ProposeNameArgs = {
  goal?: string;
  name_suggestions: string[];
};

type NameResult = {
  success: boolean;
  cancelled?: boolean;
  name?: string;
};

type PersonalityChoice =
  | {
      kind: "prebuilt";
      prebuilt: {
        slug: string;
        title: string;
        summary: string;
        instructions: string;
      };
    }
  | { kind: "custom_text"; custom_text: string }
  | { kind: "generate"; describe: string }
  | { kind: "random" };

type PersonalityResult = {
  success: boolean;
  cancelled?: boolean;
  personality?: PersonalityChoice;
};

type PersonalityMode = "prebuilt" | "generate" | "custom" | "random";

export function ProposeNameComponent({
  args,
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const draft = useAssistantDraft();
  const a = (args ?? {}) as Partial<ProposeNameArgs>;
  const suggestions = useMemo(
    () => (Array.isArray(a.name_suggestions) ? a.name_suggestions : []),
    [a.name_suggestions],
  );
  const goal = a.goal;

  const [name, setName] = useState<string>(suggestions[0] ?? "");

  const isPending = isExecuting(status);
  const settled = !isPending;
  const r = result as
    | { ok?: boolean; cancelled?: boolean; name?: string }
    | undefined;

  useEffect(() => {
    if (!isPending) return;
    return () => {
      draft.resolvePending(toolCallId, {
        success: false,
        cancelled: true,
      } satisfies NameResult);
    };
  }, [draft, toolCallId, isPending]);

  if (settled && r?.ok && !r.cancelled) {
    return (
      <ToolCard
        title="Name set"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          Name: <code className="bg-muted px-1.5 py-0.5">{r.name}</code>
        </Text>
      </ToolCard>
    );
  }

  if (settled) {
    return (
      <ToolCard title="Name — skipped">
        <Text small muted>
          No name selected. You can set one from the draft panel anytime.
        </Text>
      </ToolCard>
    );
  }

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0;

  const submit = () => {
    draft.resolvePending(toolCallId, {
      success: true,
      name: trimmedName,
    } satisfies NameResult);
  };

  const cancel = () => {
    draft.resolvePending(toolCallId, {
      success: false,
      cancelled: true,
    } satisfies NameResult);
  };

  return (
    <ToolCard
      title="Pick a name"
      icon={<Sparkles className="text-muted-foreground h-4 w-4" />}
    >
      {goal && (
        <Text small muted className="mb-3">
          Based on: {goal}
        </Text>
      )}

      <div>
        <Label className="mb-1 block text-xs font-medium">Name</Label>
        {suggestions.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setName(s)}
                className={cn(
                  "border-border hover:bg-muted rounded-full border px-2.5 py-1 text-xs transition-colors",
                  name === s &&
                    "bg-primary text-primary-foreground border-primary",
                )}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <Input
          value={name}
          onChange={setName}
          placeholder="Pick a suggestion or type your own"
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={cancel}>
          Skip
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          Save
        </Button>
      </div>
    </ToolCard>
  );
}

export function ProposePersonalityComponent({
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const draft = useAssistantDraft();
  const assistantName = draft.assistant?.name ?? "";

  const [mode, setMode] = useState<PersonalityMode>("prebuilt");
  const [prebuiltSlug, setPrebuiltSlug] = useState<string>(
    PERSONALITIES[0]?.slug ?? "",
  );
  const [describeText, setDescribeText] = useState<string>("");
  const [customText, setCustomText] = useState<string>("");

  const isPending = isExecuting(status);
  const settled = !isPending;
  const r = result as { ok?: boolean; cancelled?: boolean } | undefined;

  useEffect(() => {
    if (!isPending) return;
    return () => {
      draft.resolvePending(toolCallId, {
        success: false,
        cancelled: true,
      } satisfies PersonalityResult);
    };
  }, [draft, toolCallId, isPending]);

  if (settled && r?.ok && !r.cancelled) {
    return (
      <ToolCard
        title="Personality set"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          Personality saved.
        </Text>
      </ToolCard>
    );
  }

  if (settled) {
    return (
      <ToolCard title="Personality — skipped">
        <Text small muted>
          No personality selected. You can set one from the draft panel anytime.
        </Text>
      </ToolCard>
    );
  }

  const canSubmit =
    (mode === "prebuilt" && prebuiltSlug.length > 0) ||
    (mode === "generate" && describeText.trim().length > 0) ||
    (mode === "custom" && customText.trim().length > 0) ||
    mode === "random";

  const submit = () => {
    let personality: PersonalityChoice | undefined;
    if (mode === "prebuilt") {
      const p = getPersonality(prebuiltSlug);
      if (!p) return;
      personality = {
        kind: "prebuilt",
        prebuilt: {
          slug: p.slug,
          title: p.title,
          summary: p.summary,
          instructions: p.instructions,
        },
      };
    } else if (mode === "generate") {
      personality = { kind: "generate", describe: describeText.trim() };
    } else if (mode === "custom") {
      personality = { kind: "custom_text", custom_text: customText.trim() };
    } else {
      personality = { kind: "random" };
    }
    draft.resolvePending(toolCallId, {
      success: true,
      personality,
    } satisfies PersonalityResult);
  };

  const cancel = () => {
    draft.resolvePending(toolCallId, {
      success: false,
      cancelled: true,
    } satisfies PersonalityResult);
  };

  return (
    <ToolCard
      title="Pick a personality"
      icon={<Sparkles className="text-muted-foreground h-4 w-4" />}
    >
      {assistantName && (
        <Text small muted className="mb-3">
          For: {assistantName}
        </Text>
      )}

      <div>
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as PersonalityMode)}
          className="gap-2"
        >
          <div className="border-border border p-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="prebuilt"
                id="personality-prebuilt"
                className="mt-1"
              />
              <Label
                htmlFor="personality-prebuilt"
                className="flex-1 cursor-pointer flex-col items-start gap-0"
              >
                <Text small className="font-medium">
                  Pick a preset
                </Text>
              </Label>
            </div>
            {mode === "prebuilt" && (
              <div className="mt-3 flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <div>
                    <Text small className="font-medium">
                      Speakeasy team voices
                    </Text>
                    <Text small muted>
                      Personalities modeled on real teammates.
                    </Text>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {TEAM_PERSONALITIES.map((p) => (
                      <button
                        key={p.slug}
                        type="button"
                        onClick={() => setPrebuiltSlug(p.slug)}
                        className={cn(
                          "border-border hover:bg-muted border px-3 py-1.5 text-left transition-colors",
                          prebuiltSlug === p.slug &&
                            "border-primary bg-primary/5",
                        )}
                      >
                        <Text small className="font-medium">
                          {p.title}
                        </Text>
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const selectedTeam = TEAM_PERSONALITIES.find(
                      (p) => p.slug === prebuiltSlug,
                    );
                    return selectedTeam ? (
                      <Text small muted>
                        {selectedTeam.summary}
                      </Text>
                    ) : null;
                  })()}
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {GENERIC_PERSONALITIES.map((p) => (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => setPrebuiltSlug(p.slug)}
                      className={cn(
                        "border-border hover:bg-muted border p-2 text-left transition-colors",
                        prebuiltSlug === p.slug &&
                          "border-primary bg-primary/5",
                      )}
                    >
                      <Text small className="font-medium">
                        {p.title}
                      </Text>
                      <Text small muted className="mt-0.5">
                        {p.summary}
                      </Text>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-border border p-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="generate"
                id="personality-generate"
                className="mt-1"
              />
              <Label
                htmlFor="personality-generate"
                className="flex-1 cursor-pointer flex-col items-start gap-0"
              >
                <Text small className="font-medium">
                  Describe it in your own words
                </Text>
                <Text small muted className="mt-0.5">
                  A sentence or two — we'll expand it into a full personality.
                </Text>
              </Label>
            </div>
            {mode === "generate" && (
              <TextArea
                value={describeText}
                onChange={setDescribeText}
                placeholder="e.g. friendly but precise; uses emoji sparingly; signs off as 'yours truly'"
                rows={3}
                className="mt-2"
              />
            )}
          </div>

          <div className="border-border border p-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="custom"
                id="personality-custom"
                className="mt-1"
              />
              <Label
                htmlFor="personality-custom"
                className="flex-1 cursor-pointer flex-col items-start gap-0"
              >
                <Text small className="font-medium">
                  Paste full instructions
                </Text>
                <Text small muted className="mt-0.5">
                  Use as-is. You can always edit later.
                </Text>
              </Label>
            </div>
            {mode === "custom" && (
              <TextArea
                value={customText}
                onChange={setCustomText}
                placeholder="Full system prompt…"
                rows={5}
                className="mt-2 font-mono text-xs"
              />
            )}
          </div>

          <div className="border-border border p-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="random"
                id="personality-random"
                className="mt-1"
              />
              <Label
                htmlFor="personality-random"
                className="flex-1 cursor-pointer flex-col items-start gap-0"
              >
                <Text small className="font-medium">
                  <Shuffle className="mr-1 inline h-3.5 w-3.5" />
                  Surprise me
                </Text>
                <Text small muted className="mt-0.5">
                  Generate a fresh, unexpected personality.
                </Text>
              </Label>
            </div>
          </div>
        </RadioGroup>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={cancel}>
          Skip
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          Save
        </Button>
      </div>
    </ToolCard>
  );
}

type SlackSetupArgs = {
  preselected_capabilities?: string[];
  preselected_events?: string[];
  reply_mode?: SlackReplyMode;
  skip_slack_events?: boolean;
};

type SlackSetupSubmit = {
  success: boolean;
  cancelled?: boolean;
  capabilities?: string[];
  events?: string[];
  reply_mode?: SlackReplyMode;
};

type SlackSetupResult = {
  ok?: boolean;
  cancelled?: boolean;
  toolset_slug?: string;
  trigger_id?: string;
  webhook_url?: string;
  error?: string;
};

/**
 * Resolves the pending tool call with a cancellation if the card unmounts
 * while the user has not answered.
 */
function useCancelPendingOnUnmount(toolCallId: string, isPending: boolean) {
  const draft = useAssistantDraft();
  useEffect(() => {
    if (!isPending) return;
    return () => {
      draft.resolvePending(toolCallId, { success: false, cancelled: true });
    };
  }, [draft, toolCallId, isPending]);
}

function toggleInSet(prev: Set<string>, slug: string): Set<string> {
  const next = new Set(prev);
  if (next.has(slug)) next.delete(slug);
  else next.add(slug);
  return next;
}

function CheckboxOptionGrid({
  idPrefix,
  options,
  selected,
  onToggle,
}: {
  idPrefix: string;
  options: readonly { slug: string; label: string; description: string }[];
  selected: Set<string>;
  onToggle: (slug: string) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {options.map((g) => {
        const id = `${idPrefix}-${g.slug}`;
        const checked = selected.has(g.slug);
        return (
          <label
            key={g.slug}
            htmlFor={id}
            className={cn(
              "border-border hover:bg-muted/60 flex cursor-pointer items-start gap-2 border p-2 transition-colors",
              checked && "border-primary bg-primary/5",
            )}
          >
            <Checkbox
              id={id}
              checked={checked}
              onCheckedChange={() => onToggle(g.slug)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <Text small className="font-medium">
                {g.label}
              </Text>
              <Text small muted className="mt-0.5">
                {g.description}
              </Text>
            </div>
          </label>
        );
      })}
    </div>
  );
}

const SECTION_LABEL_CLASS =
  "mb-2 block text-xs font-medium tracking-wide uppercase";

/**
 * The reply-mode section of the Slack setup card. A reply mode the user
 * already stated shows as a summary they can reopen; otherwise the options
 * are shown up front.
 */
function SlackSetupReplySection({
  assistantName,
  stated,
  value,
  onChange,
}: {
  assistantName: string;
  stated: SlackReplyMode | undefined;
  value: SlackReplyMode;
  onChange: (value: SlackReplyMode) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(stated === undefined);
  return (
    <section>
      <Label className={SECTION_LABEL_CLASS}>
        When should {assistantName} reply?
      </Label>
      {editing ? (
        <SlackReplyModeOptions
          idPrefix="slack-setup-reply"
          value={value}
          onChange={onChange}
        />
      ) : (
        <div className="border-border flex items-start justify-between gap-2 border p-3">
          <Text small className="font-medium">
            {slackReplyModeLabel(value)}
          </Text>
          <Button variant="tertiary" size="sm" onClick={() => setEditing(true)}>
            Change
          </Button>
        </div>
      )}
    </section>
  );
}

export function ProposeSlackSetupComponent({
  args,
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const draft = useAssistantDraft();
  const a = (args ?? {}) as Partial<SlackSetupArgs>;
  const assistantName = draft.assistant?.name ?? "this assistant";
  const listens = a.skip_slack_events !== true;
  const statedReplyMode = isSlackReplyMode(a.reply_mode)
    ? a.reply_mode
    : undefined;

  const [caps, setCaps] = useState<Set<string>>(
    () =>
      new Set(
        Array.isArray(a.preselected_capabilities)
          ? a.preselected_capabilities
          : [],
      ),
  );
  const [events, setEvents] = useState<Set<string>>(
    () =>
      new Set(Array.isArray(a.preselected_events) ? a.preselected_events : []),
  );
  const [replyMode, setReplyMode] = useState<SlackReplyMode>(
    statedReplyMode ?? DEFAULT_SLACK_REPLY_MODE,
  );

  const isPending = isExecuting(status);
  const r = result as SlackSetupResult | undefined;
  useCancelPendingOnUnmount(toolCallId, isPending);

  if (!isPending && r?.cancelled) {
    return (
      <ToolCard title="Slack setup — skipped">
        <Text small muted>
          You can come back to this anytime — just ask me to set up Slack.
        </Text>
      </ToolCard>
    );
  }

  if (!isPending && r?.ok) {
    return (
      <ToolCard
        title="Slack setup saved"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          {`Created a Slack toolset for ${assistantName} and wired up its triggers.`}
        </Text>
      </ToolCard>
    );
  }

  if (!isPending) {
    return (
      <ToolCard title="Slack setup — error">
        <Text small className="text-red-600">
          {r?.error ?? "Setup did not complete."}
        </Text>
      </ToolCard>
    );
  }

  const submit = () =>
    draft.resolvePending(toolCallId, {
      success: true,
      capabilities: Array.from(caps),
      events: listens ? Array.from(events) : [],
      reply_mode: listens ? replyMode : undefined,
    } satisfies SlackSetupSubmit);

  const cancel = () =>
    draft.resolvePending(toolCallId, {
      success: false,
      cancelled: true,
    } satisfies SlackSetupSubmit);

  return (
    <ToolCard
      title="Set up Slack"
      icon={<Icon name="bot" className="text-muted-foreground h-4 w-4" />}
    >
      <Text small muted className="mb-3">
        Pick what {assistantName} can do in Slack and when it should reply. You
        can adjust this later from the assistant's settings.
      </Text>

      <div className="space-y-4">
        <section>
          <Label className={SECTION_LABEL_CLASS}>
            What can {assistantName} do?
          </Label>
          <CheckboxOptionGrid
            idPrefix="slack-cap"
            options={SLACK_CAPABILITY_GROUPS}
            selected={caps}
            onToggle={(slug) => setCaps((prev) => toggleInSet(prev, slug))}
          />
        </section>

        {listens && (
          <SlackSetupReplySection
            assistantName={assistantName}
            stated={statedReplyMode}
            value={replyMode}
            onChange={setReplyMode}
          />
        )}

        {listens && (
          <section>
            <Label className={SECTION_LABEL_CLASS}>
              Also wake {assistantName} up when… (optional)
            </Label>
            <CheckboxOptionGrid
              idPrefix="slack-event"
              options={SLACK_EVENT_GROUPS}
              selected={events}
              onToggle={(slug) => setEvents((prev) => toggleInSet(prev, slug))}
            />
          </section>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={() => void cancel()}>
          Skip
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={caps.size === 0 && !listens}
        >
          Save
        </Button>
      </div>
    </ToolCard>
  );
}

type ChooseSlackReplyModeArgs = {
  preselected?: SlackReplyMode;
};

type ChooseSlackReplyModeSubmit = {
  success: boolean;
  cancelled?: boolean;
  reply_mode?: SlackReplyMode;
};

type ChooseSlackReplyModeResult = {
  ok?: boolean;
  cancelled?: boolean;
  reply_mode?: SlackReplyMode;
  error?: string;
};

export function ChooseSlackReplyModeComponent({
  args,
  status,
  result,
  toolCallId,
}: ToolCallMessagePartProps): JSX.Element {
  const draft = useAssistantDraft();
  const a = (args ?? {}) as Partial<ChooseSlackReplyModeArgs>;
  const assistantName = draft.assistant?.name ?? "this assistant";
  const [replyMode, setReplyMode] = useState<SlackReplyMode>(
    isSlackReplyMode(a.preselected) ? a.preselected : DEFAULT_SLACK_REPLY_MODE,
  );

  const isPending = isExecuting(status);
  const r = result as ChooseSlackReplyModeResult | undefined;
  useCancelPendingOnUnmount(toolCallId, isPending);

  if (!isPending && r?.cancelled) {
    return (
      <ToolCard title="Slack replies — unchanged">
        <Text small muted>
          Nothing changed. Ask me anytime to change when it replies.
        </Text>
      </ToolCard>
    );
  }

  if (!isPending && r?.ok) {
    return (
      <ToolCard
        title="Slack replies saved"
        tone="success"
        icon={<Check className="text-emerald-600" size={16} />}
      >
        <Text small muted>
          {`${assistantName} replies ${(slackReplyModeLabel(r.reply_mode) ?? "").toLowerCase()}.`}
        </Text>
      </ToolCard>
    );
  }

  if (!isPending) {
    return (
      <ToolCard title="Slack replies — error">
        <Text small className="text-red-600">
          {r?.error ?? "Could not save the change."}
        </Text>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      title={`When should ${assistantName} reply in Slack?`}
      icon={<Icon name="bot" className="text-muted-foreground h-4 w-4" />}
    >
      <SlackReplyModeOptions
        idPrefix="slack-reply-mode"
        value={replyMode}
        onChange={setReplyMode}
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="tertiary"
          onClick={() => {
            draft.resolvePending(toolCallId, {
              success: false,
              cancelled: true,
            } satisfies ChooseSlackReplyModeSubmit);
          }}
        >
          Skip
        </Button>
        <Button
          onClick={() => {
            draft.resolvePending(toolCallId, {
              success: true,
              reply_mode: replyMode,
            } satisfies ChooseSlackReplyModeSubmit);
          }}
        >
          Save
        </Button>
      </div>
    </ToolCard>
  );
}
